import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useAuth } from '../contexts/AuthContext'
import { Button } from '../components/ui/Button'
import { Input } from '../components/ui/Input'

const loginSchema = z.object({
  username: z.string().min(1, 'Användarnamn krävs').trim(),
  password: z.string().min(1, 'Lösenord krävs').trim(),
})

type LoginForm = z.infer<typeof loginSchema>

export const LoginPage: React.FC = () => {
  const { login, error: authError, isLoading } = useAuth()
  const navigate = useNavigate()
  const [loginError, setLoginError] = useState<string>('')

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
    watch,
  } = useForm<LoginForm>({
    resolver: zodResolver(loginSchema),
    mode: 'onChange',
  })

  // Watch form values to debug
  const watchedValues = watch()

  // Debug logging
  console.log('Form values:', watchedValues)
  console.log('Form errors:', errors)
  console.log('Form isValid:', isValid)

  const onSubmit = async (data: LoginForm) => {
    try {
      setLoginError('')
      await login(data)
      navigate('/')
    } catch (error) {
      setLoginError(error instanceof Error ? error.message : 'Inloggning misslyckades')
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        {/* Logo */}
        <div className="text-center">
          <img src="/ntr_black.png" alt="NTR" className="mx-auto h-12 w-auto" />
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            Logga in
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Träningssystem för NTR
          </p>
        </div>

        {/* Login Form */}
        <form className="mt-8 space-y-6" onSubmit={handleSubmit(onSubmit)}>
          <div className="space-y-4">
            <Input
              label="Användarnamn"
              type="text"
              autoComplete="username"
              error={errors.username?.message}
              {...register('username')}
            />

            <Input
              label="Lösenord"
              type="password"
              autoComplete="current-password"
              error={errors.password?.message}
              {...register('password')}
            />

            {/* Debug Info - Remove this after testing */}
            <div className="text-xs text-gray-500 bg-gray-100 p-2 rounded">
              <div>Username: "{watchedValues.username || ''}"</div>
              <div>Password: "{watchedValues.password ? '*'.repeat(watchedValues.password.length) : ''}"</div>
              <div>Errors: {Object.keys(errors).length > 0 ? 'Yes' : 'No'}</div>
              <div>Is Valid: {isValid ? 'Yes' : 'No'}</div>
            </div>
          </div>

          {/* Error Messages */}
          {(loginError || authError) && (
            <div className="rounded-md bg-danger-50 p-4">
              <div className="text-sm text-danger-700">
                {loginError || authError}
              </div>
            </div>
          )}

          {/* Submit Button */}
          <div className="space-y-3">
            <Button
              type="submit"
              className="w-full"
              isLoading={isLoading}
              disabled={isLoading}
            >
              {isLoading ? 'Loggar in...' : 'Logga in'}
            </Button>

            {/* Test Button - Remove this after testing */}
            <Button
              type="button"
              variant="outline"
              className="w-full"
              onClick={() => {
                console.log('Manual test - Form values:', watchedValues)
                console.log('Manual test - Errors:', errors)
                if (watchedValues.username && watchedValues.password) {
                  onSubmit(watchedValues)
                } else {
                  alert('Please fill in both fields')
                }
              }}
            >
              Test Login (Bypass Validation)
            </Button>
          </div>
        </form>

        {/* Footer */}
        <div className="text-center">
          <p className="text-sm text-gray-500">
            NTR Träningssystem v1.0.0
          </p>
        </div>
      </div>
    </div>
  )
}
